import React, { useState, useEffect } from 'react';
import { Drawer, Button, Input, Select, Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { PreconditionRule } from '@troia-v3/types';

// ─── Constantes ──────────────────────────────────────────────────────────────

const RULE_TYPE_OPTIONS = [
  { value: 'questions_answered', label: 'Perguntas respondidas' },
  { value: 'context_check', label: 'Verificação de contexto' },
  { value: 'tag_present', label: 'Tag presente' },
  { value: 'field_value', label: 'Valor de campo' },
  { value: 'time_window', label: 'Janela de tempo' },
];

const OPERATOR_OPTIONS = [
  { value: '=', label: 'Igual a (=)' },
  { value: '!=', label: 'Diferente de (≠)' },
  { value: '>', label: 'Maior que (>)' },
  { value: '<', label: 'Menor que (<)' },
  { value: 'contains', label: 'Contém' },
];

const DAY_OPTIONS = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
];

// ─── Estado do formulário ────────────────────────────────────────────────────

interface FormState {
  ruleType: PreconditionRule['type'] | '';
  // questions_answered
  questions: string[];
  // context_check
  contextQuestion: string;
  // tag_present
  tag: string;
  // field_value
  field: string;
  operator: '=' | '!=' | '>' | '<' | 'contains';
  fieldValue: string;
  // time_window
  startHour: string;
  endHour: string;
  allowedDays: number[];
}

const DEFAULT_FORM: FormState = {
  ruleType: '',
  questions: [''],
  contextQuestion: '',
  tag: '',
  field: '',
  operator: '=',
  fieldValue: '',
  startHour: '8',
  endHour: '18',
  allowedDays: [1, 2, 3, 4, 5],
};

function formStateFromRule(rule: PreconditionRule): FormState {
  const base: FormState = { ...DEFAULT_FORM, ruleType: rule.type };
  switch (rule.type) {
    case 'questions_answered':
      return { ...base, questions: rule.questions.length > 0 ? rule.questions : [''] };
    case 'context_check':
      return { ...base, contextQuestion: rule.question };
    case 'tag_present':
      return { ...base, tag: rule.tag };
    case 'field_value':
      return { ...base, field: rule.field, operator: rule.operator, fieldValue: rule.value };
    case 'time_window':
      return {
        ...base,
        startHour: String(rule.startHour),
        endHour: String(rule.endHour),
        allowedDays: rule.allowedDays ?? [0, 1, 2, 3, 4, 5, 6],
      };
    default:
      return base;
  }
}

function buildRule(form: FormState): PreconditionRule | null {
  switch (form.ruleType) {
    case 'questions_answered': {
      const questions = form.questions.map((q) => q.trim()).filter(Boolean);
      if (questions.length === 0) return null;
      return { type: 'questions_answered', questions };
    }
    case 'context_check': {
      if (!form.contextQuestion.trim()) return null;
      return { type: 'context_check', question: form.contextQuestion.trim() };
    }
    case 'tag_present': {
      if (!form.tag.trim()) return null;
      return { type: 'tag_present', tag: form.tag.trim() };
    }
    case 'field_value': {
      if (!form.field.trim() || !form.fieldValue.trim()) return null;
      return {
        type: 'field_value',
        field: form.field.trim(),
        operator: form.operator,
        value: form.fieldValue.trim(),
      };
    }
    case 'time_window': {
      const start = parseInt(form.startHour, 10);
      const end = parseInt(form.endHour, 10);
      if (isNaN(start) || isNaN(end) || start < 0 || end > 23 || start >= end) return null;
      const allowedDays =
        form.allowedDays.length < 7
          ? [...form.allowedDays].sort((a, b) => a - b)
          : undefined;
      return { type: 'time_window', startHour: start, endHour: end, ...(allowedDays ? { allowedDays } : {}) };
    }
    default:
      return null;
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface PreconditionFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: PreconditionRule) => void;
  existingRule?: PreconditionRule;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export const PreconditionFormDrawer: React.FC<PreconditionFormDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  existingRule,
}) => {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [error, setError] = useState<string>('');

  // Preencher ao abrir em modo edição
  useEffect(() => {
    if (isOpen) {
      setError('');
      setForm(existingRule ? formStateFromRule(existingRule) : { ...DEFAULT_FORM });
    }
  }, [isOpen, existingRule]);

  const isEdit = !!existingRule;

  const handleSave = () => {
    const rule = buildRule(form);
    if (!rule) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }
    onSave(rule);
    onClose();
  };

  // ─── Helpers para questions_answered ──
  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, ''] }));
  const removeQuestion = (idx: number) =>
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  const updateQuestion = (idx: number, value: string) =>
    setForm((f) => {
      const qs = [...f.questions];
      qs[idx] = value;
      return { ...f, questions: qs };
    });

  // ─── Helpers para time_window days ──
  const toggleDay = (day: number) => {
    setForm((f) => {
      const next = f.allowedDays.includes(day)
        ? f.allowedDays.filter((d) => d !== day)
        : [...f.allowedDays, day];
      return { ...f, allowedDays: next };
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Editar regra' : 'Nova regra'}
      icon={<Icon name="filter" className="w-4 h-4" />}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            {isEdit ? 'Salvar alterações' : 'Adicionar regra'}
          </Button>
        </>
      }
    >
      <div className="space-y-5 px-6 py-5">
        {/* Tipo da regra */}
        <Select
          label="Tipo da regra"
          placeholder="Selecione o tipo..."
          options={RULE_TYPE_OPTIONS}
          value={form.ruleType}
          onChange={(v) => {
            setError('');
            setForm({ ...DEFAULT_FORM, ruleType: v as PreconditionRule['type'] });
          }}
          fullWidth
        />

        {/* ─── questions_answered ─── */}
        {form.ruleType === 'questions_answered' && (
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Perguntas que o cliente deve ter respondido
            </label>
            <div className="space-y-2">
              {form.questions.map((q, idx) => (
                <div key={idx} className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder={`Pergunta ${idx + 1}`}
                      value={q}
                      onChange={(e) => updateQuestion(idx, e.target.value)}
                      fullWidth
                    />
                  </div>
                  {form.questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(idx)}
                      className="p-2 rounded-lg transition-colors flex-shrink-0"
                      style={{ color: '#ef4444' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      title="Remover pergunta"
                    >
                      <Icon name="x" className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addQuestion}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: '#8b5cf6' }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Icon name="plus" className="w-4 h-4" />
              Adicionar pergunta
            </button>
          </div>
        )}

        {/* ─── context_check ─── */}
        {form.ruleType === 'context_check' && (
          <Textarea
            label="Pergunta de verificação"
            description="O LLM analisará o histórico e responderá sim/não a esta pergunta."
            placeholder="Ex: O cliente informou o endereço de entrega?"
            value={form.contextQuestion}
            onChange={(e) => setForm((f) => ({ ...f, contextQuestion: e.target.value }))}
            rows={3}
            fullWidth
          />
        )}

        {/* ─── tag_present ─── */}
        {form.ruleType === 'tag_present' && (
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
              Tag do contato
            </label>
            <p className="text-xs mb-2" style={{ color: '#64748b' }}>
              A ferramenta só será executada se o contato possuir esta tag.
            </p>
            <Input
              placeholder="Ex: cliente-vip"
              value={form.tag}
              onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
              fullWidth
            />
          </div>
        )}

        {/* ─── field_value ─── */}
        {form.ruleType === 'field_value' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
                Campo
              </label>
              <p className="text-xs mb-2" style={{ color: '#64748b' }}>
                Caminho do campo no objeto de contexto. Ex: lead.status
              </p>
              <Input
                placeholder="Ex: contact.segment"
                value={form.field}
                onChange={(e) => setForm((f) => ({ ...f, field: e.target.value }))}
                fullWidth
              />
            </div>
            <Select
              label="Operador"
              options={OPERATOR_OPTIONS}
              value={form.operator}
              onChange={(v) => setForm((f) => ({ ...f, operator: v as FormState['operator'] }))}
              fullWidth
            />
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
                Valor
              </label>
              <Input
                placeholder="Ex: qualificado"
                value={form.fieldValue}
                onChange={(e) => setForm((f) => ({ ...f, fieldValue: e.target.value }))}
                fullWidth
              />
            </div>
          </div>
        )}

        {/* ─── time_window ─── */}
        {form.ruleType === 'time_window' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
                  Hora de início (0–23)
                </label>
                <Input
                  type="number"
                  placeholder="8"
                  value={form.startHour}
                  onChange={(e) => setForm((f) => ({ ...f, startHour: e.target.value }))}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
                  Hora de fim (0–23)
                </label>
                <Input
                  type="number"
                  placeholder="18"
                  value={form.endHour}
                  onChange={(e) => setForm((f) => ({ ...f, endHour: e.target.value }))}
                  fullWidth
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                Dias permitidos
              </label>
              <p className="text-xs mb-3" style={{ color: '#64748b' }}>
                Se nenhum dia for selecionado, a regra valerá para todos os dias.
              </p>
              <div className="flex flex-wrap gap-2">
                {DAY_OPTIONS.map((day) => {
                  const active = form.allowedDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      onClick={() => toggleDay(day.value)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors"
                      style={{
                        backgroundColor: active ? '#8b5cf6' : '#ffffff',
                        borderColor: active ? '#8b5cf6' : '#e5e7eb',
                        color: active ? '#ffffff' : '#374151',
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Erro de validação */}
        {error && (
          <p className="text-sm" style={{ color: '#ef4444' }}>
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
};
